import React, { useMemo } from "react";
import { CircleSlash } from "lucide-react";
import { useAgents } from "@/shared/api/agents";
import PixelAvatar from "@/shared/components/PixelAvatar";
import TranscriptDivider from "@/shared/components/TranscriptDivider";
import type { Message } from "@/shared/types";

/** Builder's transcript: above each person message about another agent than their message
    before, a line saying so — "Working on X" for the first agent, "Now working on X" after, "No
    agent picked" when the picker was cleared — keyed by the message. The agent goes by the name
    the workspace gives it now, as the picker does (the saved one once it is gone). */
export default function useAgentSwitches(messages: Message[]): Map<string, React.ReactNode> {
  const { data: agents } = useAgents();
  return useMemo(() => {
    const lines = new Map<string, React.ReactNode>();
    let last: string | null | undefined; // the previous person message's agent; undefined before the first
    let named = false; // a line has named an agent
    for (const m of messages) {
      if (m.role !== "user") continue;
      const about = m.contextAgent ?? null;
      const id = about?.id ?? null;
      const unchanged = id === last || (last === undefined && id === null);
      last = id;
      if (unchanged) continue;
      if (!about) {
        lines.set(m.id, <TranscriptDivider icon={CircleSlash} label="No agent picked">No agent picked</TranscriptDivider>);
        continue;
      }
      const listed = agents?.find((a) => a.id === about.id);
      const words = `${named ? "Now working on" : "Working on"} ${listed?.name ?? about.name}`;
      lines.set(m.id, (
        <TranscriptDivider lead={<PixelAvatar seed={listed?.seed ?? about.id} size={14} ring />} label={words}>{words}</TranscriptDivider>
      ));
      named = true;
    }
    return lines;
  }, [messages, agents]);
}
